import { useStore } from '@nanostores/react';
import { useAuth } from '@/services/auth';
import { AccountState } from '@/services/auth/types';
import { billing } from '@/services/billing';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { isEmptyString } from '@/utils/string';
import { useCardTabHref } from './use-card-tab-href';

/**
 * What the billing-alert banner states. The lump-sum overage wins when both apply, so the model
 * carries one branch, not two flags for the banner to re-decide.
 */
export type BillingAlertModel =
  | { kind: 'lump-sum-overage'; onUpdateBilling: () => void }
  | { kind: 'payment-error'; message: string };

/**
 * The billing-alert banner's data. An overdue account either has a failed lump-sum overage charge or
 * a payment error to state; anything else returns null so the banner renders nothing.
 */
export function useBillingAlertBanner(): BillingAlertModel | null {
  const { account } = useAuth();
  const navigate = useSafeNavigate();
  const isLumpSumPlan = useStore(billing.isLumpSumPlan);
  const hasFailedOverageCharge = useStore(billing.hasFailedOverageCharge);
  const lastPaymentError = useStore(billing.lastPaymentError);

  const cardTab = useCardTabHref();

  const isOverdue = account?.account_state === AccountState.Overdue;
  if (!isOverdue) {
    return null;
  }

  if (isLumpSumPlan && hasFailedOverageCharge) {
    return { kind: 'lump-sum-overage', onUpdateBilling: () => navigate(cardTab) };
  }

  if (!isEmptyString(lastPaymentError)) {
    return { kind: 'payment-error', message: lastPaymentError ?? '' };
  }

  return null;
}
